import { CSSProperties, isValidElement, ReactNode, useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { Icon } from 'ui/atoms/Icon/Icon';
import { AnimatedNumber } from 'ui/atoms/AnimatedNumber/AnimatedNumber';

import styles from './FeatureGrid.module.scss';

const baseClass = 'gdb-feature-grid';

export type FeatureGridItem = {
  icon?: string | ReactNode;
  content?: ReactNode;
  title?: string;
  description?: ReactNode;
  value?: string;
  suffix?: string;
  prefix?: string;
  link?: {
    href: string;
    label?: string;
  };
};

export type FeaturesTheme = 'light' | 'dark' | 'transparent' | 'stats-card';

export type FeatureGridTextSize = 'md' | 'lg' | 'xl';

export type FeatureGridLayout = '2-3-2' | '2-3' | '3-2' | '3-3';

type Props = {
  items: FeatureGridItem[];
  theme?: FeaturesTheme;
  hasBorder?: boolean;
  hasPadding?: boolean;
  hasCheckmark?: boolean;
  separators?: 'vertical' | 'top' | 'wrap';
  columns?: number;
  align?: 'center' | 'left' | 'right';
  textSize?: FeatureGridTextSize;
  layout?: FeatureGridLayout;
  animated?: boolean;
  className?: string;
};

export const FeatureGrid = ({
  items,
  theme = 'light',
  hasBorder = false,
  hasPadding = true,
  hasCheckmark = true,
  separators = 'vertical',
  columns = 3,
  align = 'center',
  textSize = 'md',
  layout,
  animated = true,
  className,
}: Props) => {
  const isWrap = separators === 'wrap';
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersection(ref, { threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={cn(
        styles[`${baseClass}-wrapper`],
        styles[`${baseClass}-${theme}`],
        animated && styles[`${baseClass}--animated`],
        animated && visible && styles[`${baseClass}--visible`],
        textSize !== 'md' && styles[`${baseClass}--text-${textSize}`],
        hasBorder && styles[`${baseClass}-border`],
        !hasPadding && styles[`${baseClass}--no-padding`],
        separators === 'top' && styles[`${baseClass}--separators-top`],
        isWrap && styles[`${baseClass}--separators-wrap`],
        isWrap && !layout && styles[`${baseClass}--cols-${columns}`],
        isWrap && layout && styles[`${baseClass}--layout-${layout}`],
        align !== 'center' && styles[`${baseClass}--align-${align}`],
        className,
      )}
      style={isWrap ? ({ '--fg-cols': columns } as CSSProperties) : undefined}
    >
      <div className={styles[`${baseClass}-container`]}>
        {items.map((item, idx) => {
          let content: ReactNode = item.content;

          if (!content && !item.value && !item.title) {
            return null;
          }

          if (!content && item.value) {
            content = (
              <div className={styles[`${baseClass}-stats-item`]}>
                {item.prefix && <p>{item.prefix}</p>}

                {isNaN(parseInt(item.value)) || !animated ? (
                  <p>
                    {item.value}
                    {item.suffix}
                  </p>
                ) : (
                  <AnimatedNumber value={parseInt(item.value)} suffix={item.suffix} />
                )}

                {hasCheckmark && <Icon type="check-2" />}
              </div>
            );
          }

          return (
            <div key={idx} className={styles[`${baseClass}-item`]}>
              {item.icon &&
                (isValidElement(item.icon) ? (
                  <div className={styles[`${baseClass}-icon-jsx`]}>{item.icon}</div>
                ) : (
                  <Icon type={item.icon as string} className={styles[`${baseClass}-icon`]} />
                ))}

              {content && <div className={styles[`${baseClass}-content`]}>{content}</div>}

              <div className={styles[`${baseClass}-title`]}>{item.title}</div>
              {item.description && <div className={styles[`${baseClass}-desc`]}>{item.description}</div>}

              {item.link && (
                <a href={item.link.href} className={styles[`${baseClass}-link`]}>
                  {item.link.label || 'Learn more'}
                  <Icon type="arrow-14" className={styles[`${baseClass}-link-icon`]} />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
